import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

import type { WorkflowFallback } from './routePlans';
import type {
  RouteStopIntent,
  RouteStopRole,
  RouteStopType,
} from './routeStopKind';
import type { CreateDirect4WRider } from './shipments';
import type { TariffTipe } from './tariffs';

/**
 * UJP — Usulan Jasa Pengangkutan (nest-logistic-service, `/v1/ujp`).
 *
 * The per-trip running-cost request that authorizes a driver's uang jalan.
 * Approving a driver-payee UJP creates the DIRECT_4W shipment in the same
 * transaction; a subcon approval records the decision only.
 *
 * Every response rides the `{ status: 'Success', data }` envelope; errors are
 * `{ status: 'Failed', error }` with 400 / 403 / 404 / 409. Money travels as
 * STRINGS (numeric(14,2) on the wire) — never do arithmetic on them in the
 * browser: the server owns the formula (`POST /v1/ujp/estimate`) and recomputes
 * on create; anything the client sends as a total is ignored.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary
// ─────────────────────────────────────────────────────────────────────────────

export type UjpStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export const UJP_STATUSES: readonly UjpStatus[] = [
  'SUBMITTED',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
];

export type UjpPayeeType = 'driver' | 'subcon';
export type UjpEnergyType = 'ICE' | 'EV';
export const UJP_ENERGY_TYPES: readonly UjpEnergyType[] = ['ICE', 'EV'];
export type UjpFuelType = 'SOLAR' | 'DEXLITE' | 'PERTALITE' | 'EV_KWH';
export const UJP_FUEL_TYPES: readonly UjpFuelType[] = [
  'SOLAR',
  'DEXLITE',
  'PERTALITE',
  'EV_KWH',
];
/** The fuel types a vehicle of each energy type may carry (mirrors the DTO rule). */
export const UJP_FUEL_TYPES_BY_ENERGY: Record<UjpEnergyType, readonly UjpFuelType[]> = {
  ICE: ['SOLAR', 'DEXLITE', 'PERTALITE'],
  EV: ['EV_KWH'],
};
/** E-money buckets (CR-D4). FLAZZ and NONE reproduce the pre-QRIS numbers. */
export type UjpEMoney = 'NONE' | 'FLAZZ' | 'QRIS' | 'FLAZZ_QRIS';
export type UjpDeliveryType = 'SINGLE_DROP' | 'MULTI_DROP';
/**
 * Legacy stop role on a UJP route (CR-D2) — DERIVED since CR-6 (§22): the
 * truth is `type` + `intent` + `pool` (see `routeStopKind.ts`). POSITIONING =
 * PICKUP·pool, RETURN = DROP_OFF·pool; legs touching a pool stop are the "pool
 * legs" a client may or may not be charged for (CR-D7).
 */
export type UjpStopRole = RouteStopRole;

/** Money on the wire — a decimal string such as `"695280.00"`. */
export type MoneyString = string;

/**
 * Km / litres / baseline on the wire. The folder contract is explicit —
 * "All money values are strings (integer rupiah); km/litres are decimal
 * strings" — so every numeric decimal is SENT as a string. Responses are typed
 * permissively because a JSON number is equally valid on the way back; read
 * them through `toNum` (pages/ujp/format) rather than doing arithmetic on the
 * raw field.
 */
export type NumberLike = string | number;

// ─────────────────────────────────────────────────────────────────────────────
// Route
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpRouteStop {
  seq: number;
  /** CR-6 workflow vocabulary; absent only on a pre-CR-6 snapshot → `kindOf()` derives from `role`. */
  type?: RouteStopType | null;
  /** PICKUP is always GENERIC; DROP_OFF defaults to UNLOAD (GENERIC when pool). */
  intent?: RouteStopIntent | null;
  /**
   * The empty pool leg. Since CR-8 (D5) EVERY stop, pool included, reaches the
   * shipment; billing only decides whether its legs count towards `kmCharged`.
   */
  pool?: boolean | null;
  /**
   * CR-8 (D1): billing override for a pool stop. `null`/absent = follow the
   * client rule; `true`/`false` wins over it (D2). Sent on pool stops only;
   * the detail echoes it (null when unset).
   */
  charged?: boolean | null;
  /** DERIVED legacy role — echoed by the server; the wizard sends it alongside for now. */
  role: UjpStopRole;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  /** CR-5: workflow pinned on the route plan this stop came from; `null` = client default. */
  workflowId?: string | null;
  /** Display name of the pinned workflow (as stored on the plan snapshot). */
  workflowName?: string | null;
}

export interface UjpRouteLeg {
  seq: number;
  fromSeq: number;
  toSeq: number;
  /** Decimal string on the way out (`"62.4"`). */
  km: NumberLike;
}

export interface UjpRoutePayload {
  stops: UjpRouteStop[];
  legs: UjpRouteLeg[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Estimate
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpEstimateHeader {
  clientId: number;
  /** `YYYY-MM-DD` — selects the energy price effective on that day. */
  deliveryDate: string;
  isReverse: boolean;
}

export interface UjpEstimatePayee {
  type: UjpPayeeType;
  /** Subcon: the single required money field. */
  nominalTransfer?: MoneyString;
}

export interface UjpEstimateVehicle {
  energyType: UjpEnergyType;
  fuelType?: UjpFuelType;
  /** ICE: km per litre — decimal string (`"4.5"`). */
  baseline?: NumberLike;
  /** EV: kWh per km — decimal string. */
  konsumsiPerKm?: NumberLike;
  /** Rp per litre / kWh — from the master, editable. */
  energyPrice: MoneyString;
  eMoney: UjpEMoney;
}

/** How the server pays one cost row: the vehicle's Flazz card or a transfer. */
export type UjpCostItemPayment = 'FLAZZ' | 'TRANSFER';

/** One cost row as ops typed it (PLAN-UJP-DYNAMIC-COSTS). */
export interface UjpCostItem {
  label: string;
  amount: MoneyString;
}

/** A cost row as the server priced it: the payment comes from the row's name. */
export interface UjpPricedCostItem extends UjpCostItem {
  payment: UjpCostItemPayment;
}

export interface UjpEstimateCosts {
  /** Decimal string (`"141.2"`). */
  kmYangDiajukan: NumberLike;
  bbmFixOverride?: MoneyString;
  /** Required by the server (`[]` when there are none). */
  costItems: UjpCostItem[];
}

export interface UjpEstimatePayload {
  header: UjpEstimateHeader;
  payee: UjpEstimatePayee;
  vehicle: UjpEstimateVehicle;
  costs: UjpEstimateCosts;
  route: UjpRoutePayload;
}

export interface UjpEstimateResult {
  totalKmWithMargin: NumberLike;
  estimasiBbmLiter: NumberLike;
  totalBbmCost: MoneyString;
  totalUangJalanFlazz: MoneyString;
  totalUangJalanQris: MoneyString;
  totalUangJalanTransfer: MoneyString;
  estimatedAmount: MoneyString;
  kmAllLegs: NumberLike;
  kmCharged: NumberLike;
  kmMarginPct: NumberLike;
  /** CR-8: "some pool leg on this route is billed" (the client rule when there is no pool stop). */
  chargedPositioning: boolean;
  /**
   * CR-8 (D3): a pool stop's effective billing differs from the client rule.
   * The UI shows "Beda dari aturan client" wherever km is shown. Optional so a
   * pre-CR-8 server (and the collection example) still reads as "no".
   */
  chargedPositioningOverride?: boolean;
  /** The "Biaya reverse (client)" line actually added (`"0.00"` when none). */
  reverseChargeApplied: MoneyString;
  /** Each cost row with its payment — the "Dibayar: Flazz/Transfer" hint. */
  costItems?: UjpPricedCostItem[];
  energyPriceSource?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Create
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpCreateHeader extends UjpEstimateHeader {
  serviceType?: string;
  deliveryType: UjpDeliveryType;
  shift?: string;
  /** Region for fuel-price resolution; null/omit = global default. Snapshotted. */
  region?: string | null;
  /** Revenue tags (CR-4). The ring is only meaningful for a PER_RING client. */
  tariffConfigId?: string | null;
  ringId?: string | null;
}

export interface UjpCreatePayee extends UjpEstimatePayee {
  subconVendorId?: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

export interface UjpCreateVehicle extends UjpEstimateVehicle {
  plateNumber: string;
  unitType?: string;
  /** Physical e-money card used for THIS trip — a per-UJP note (nullable). */
  eMoneyCardNo?: string | null;
  vehicleId?: string;
}

export interface UjpCreateCosts extends UjpEstimateCosts {
  /** Optional "Catatan biaya" for the approver (kept its lain-lain name). */
  justifikasiBiayaLainLain?: string | null;
}

/**
 * The detail's `costs` block (CR-8): the create fields plus the two billing
 * flags the server froze at submit. Optional so a pre-CR-8 row still reads;
 * the panel falls back to `estimate.chargedPositioning` / the client rule.
 */
export interface UjpDetailCosts extends UjpCreateCosts {
  /** The stored rows with the payment each was priced under. */
  costItems: UjpPricedCostItem[];
  /** Some pool leg on this UJP is billed (what `kmCharged` was computed with). */
  chargedPositioningApplied?: boolean;
  /** A pool stop's billing differs from the client rule (D3). */
  chargedPositioningOverride?: boolean;
}

export interface UjpCreateRoute extends UjpRoutePayload {
  /**
   * The route plan this snapshot came from (CR-2). Absent for an ad-hoc route;
   * routes are created only through the planner/drawer now, so `saveAs` is gone.
   */
  routePlanId?: string;
}

export interface CreateUjpPayload {
  header: UjpCreateHeader;
  payee: UjpCreatePayee;
  vehicle: UjpCreateVehicle;
  costs: UjpCreateCosts;
  route: UjpCreateRoute;
  /** Required for a driver payee; optional for subcon. */
  rider?: CreateDirect4WRider;
}

export interface CreateUjpResult {
  id: string;
  referenceId: string;
  routePlanId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// List
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpListParams {
  status?: UjpStatus;
  clientId?: number;
  deliveryFrom?: string;
  deliveryTo?: string;
  search?: string;
  /**
   * Only UJPs whose own or a biaya tambahan's Spend Control request is FAILED
   * or UNKNOWN ("Spend Control bermasalah"). Part of the scope, so the tab
   * counts follow it.
   */
  spendControlIssue?: boolean;
  page?: number;
  limit?: number;
}

export interface UjpTotals {
  flazz: MoneyString;
  qris: MoneyString;
  transfer: MoneyString;
  total: MoneyString;
  /**
   * CR-7: Σ APPROVED biaya tambahan (`"0.00"` when none; null when masked).
   * Detail only — the list row totals never carry it.
   */
  amended?: MoneyString | null;
  /** CR-7: `total + amended`, what the trip has actually cost so far. */
  totalWithAmendments?: MoneyString | null;
}

export interface UjpNamedRef {
  id: string | number;
  name: string;
}

export interface UjpShipmentRef {
  id: string;
  waybill: string;
  status: string;
  /**
   * Detail only. `List UJP` LEFT JOINs `{ id, waybill, status }`; `Detail UJP`
   * documents `{ id, waybill, routeCode, status }`. Optional so one type serves
   * both without a row pretending to carry a route code it never had.
   */
  routeCode?: string;
}

export interface UjpRow {
  id: string;
  referenceId: string;
  status: UjpStatus;
  client: { id: number; name: string };
  deliveryDate: string;
  deliveryType: UjpDeliveryType;
  isReverse: boolean;
  origin: string;
  destination: string;
  stopCount: number;
  payeeType: UjpPayeeType;
  rider: UjpNamedRef | null;
  vendor: UjpNamedRef | null;
  plateNumber: string;
  eMoney: UjpEMoney;
  /** Null when the caller is neither requester nor approver (masked). */
  totals: UjpTotals | null;
  masked: boolean;
  /** Approver-only revenue/margin (CR-4); null / absent for everyone else. */
  revenue?: UjpRevenue | null;
  /** CR-7: SUBMITTED biaya tambahan waiting on finance (0 / absent when none). */
  pendingAmendments?: number;
  /**
   * The worst Spend Control state across the UJP and its biaya tambahan —
   * FAILED already wins over UNKNOWN server-side; null / absent when fine.
   */
  spendControlIssue?: UjpSpendControlIssue | null;
  createdAt: string;
  requesterEmail: string;
  decidedBy: string | null;
  decidedAt: string | null;
  shipment: UjpShipmentRef | null;
}

/**
 * Revenue availability (CR4-D10). `OK` is the only state that yields a margin
 * and can trigger the negative-margin confirm; the rest mean "revenue belum
 * bisa dihitung" and never fabricate a number.
 */
export type UjpRevenueStatus =
  | 'OK'
  | 'MISSING_TARIFF'
  | 'UNSUPPORTED_MODE'
  | 'MISSING_RING'
  /** DISTANCE_TIER config with no tiers (CR4b-D3). */
  | 'MISSING_TIER';

/**
 * Revenue note codes (CR4-D9 / CR4b-D11). The wire `note` is a string holding
 * ZERO OR MORE of these joined by `,` — split it with {@link parseRevenueNotes}.
 *  - `MULTI_RATE_FALLBACK` — multi-drop rate 0/unset, the single rate was used
 *  - `BODY_RATE_DEFAULT`   — overrides exist but the unit type matched none
 *  - `TIER_ABOVE_MAX`      — km above every tier, the highest tier was used
 *  - `RING_AUTO_SINGLE`    — no ring tagged, the client's only ACTIVE ring was used
 */
export type UjpRevenueNoteCode =
  | 'MULTI_RATE_FALLBACK'
  | 'BODY_RATE_DEFAULT'
  | 'TIER_ABOVE_MAX'
  | 'RING_AUTO_SINGLE';
export const UJP_REVENUE_NOTE_CODES: readonly UjpRevenueNoteCode[] = [
  'MULTI_RATE_FALLBACK',
  'BODY_RATE_DEFAULT',
  'TIER_ABOVE_MAX',
  'RING_AUTO_SINGLE',
];
export const isUjpRevenueNoteCode = (v: string): v is UjpRevenueNoteCode =>
  (UJP_REVENUE_NOTE_CODES as readonly string[]).includes(v);

/**
 * `"MULTI_RATE_FALLBACK,BODY_RATE_DEFAULT"` → the known codes, deduped, in wire
 * order. Tolerates the CR-4 single-code form, blanks, whitespace and null.
 * Unknown tokens are dropped: every code the UI shows must have a label.
 */
export const parseRevenueNotes = (
  note: string | null | undefined
): UjpRevenueNoteCode[] => {
  if (!note) return [];
  const out: UjpRevenueNoteCode[] = [];
  for (const raw of note.split(',')) {
    const code = raw.trim();
    if (isUjpRevenueNoteCode(code) && !out.includes(code)) out.push(code);
  }
  return out;
};

/** Where the applied rate came from (CR4b-D10 resolution order). */
export type UjpRateSource =
  | 'BODY_MULTI'
  | 'BASE_MULTI'
  | 'BODY_SINGLE'
  | 'BASE_SINGLE'
  | 'TIER'
  | 'FIXED';

/**
 * The tariff a UJP's revenue was priced on, frozen at approve (CR-4 / CR-4b
 * §20.3). Additive: pre-CR-4b snapshots lack the `rateApplied…notes` keys, so
 * those are optional here and the panel renders what is present.
 */
export interface UjpTariffSnapshot {
  tariffConfigId: string;
  tipe: TariffTipe;
  rateFlat: MoneyString | null;
  rateFlatMulti: MoneyString | null;
  rateFixed: MoneyString | null;
  asuransi: MoneyString;
  // ── CR-4b ──────────────────────────────────────────────────────────────────
  rateApplied?: MoneyString | null;
  rateSource?: UjpRateSource | null;
  /** The unit-type key that matched an override, else null. */
  bodyKey?: string | null;
  tier?: { maxKm: number; rate: MoneyString } | null;
  surcharge?: {
    titik: number;
    freePoints: number;
    extraRate: MoneyString;
    amount: MoneyString;
  };
  reverseAmount?: MoneyString;
  /** `applied` = what was subtracted from margin (driver only, CR4b-D4). */
  komisi?: { driver: MoneyString; helper: MoneyString; applied: MoneyString };
  ring: {
    id: string;
    nama: string;
    ratePerTrip: MoneyString | null;
    ratePerTripMulti: MoneyString | null;
  } | null;
  notes?: string[];
}

/** Approver-only revenue block (field-tier masking, CR4-D4). */
export interface UjpRevenue {
  amount: MoneyString | null;
  /** `revenue − cost − komisi` (CR4b-D4). */
  margin: MoneyString | null;
  status: UjpRevenueStatus;
  /** Zero or more {@link UjpRevenueNoteCode}s joined by `,` (CR4b-D11). */
  note?: string | null;
  /** Komisi subtracted from the margin (driver share; null when none). */
  komisi?: MoneyString | null;
  /** The tariff snapshot the revenue was computed from. */
  tariff?: UjpTariffSnapshot | null;
}

export type UjpCountKey = UjpStatus | 'ALL';

export interface UjpListResponse {
  data: UjpRow[];
  pagination: { size: number; page: number; lastPage: number; total: number };
  counts: Record<UjpCountKey, number>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpFieldChange {
  field: string;
  from: string | null;
  to: string | null;
}

export interface UjpHistoryEntry {
  at: string;
  by: string;
  from: UjpStatus | null;
  to: UjpStatus;
  note: string | null;
  reasonCode: string | null;
  /**
   * The per-field diff of an edit (CR3-D4). Present on EDITED / RESUBMITTED
   * rows; money fields are already masked server-side for non-parties.
   */
  changes?: UjpFieldChange[] | null;
}

export interface UjpViewer {
  canDecide: boolean;
  canCancel: boolean;
  isRequester: boolean;
  isApprover: boolean;
  /**
   * CR-7: may raise a biaya tambahan now — the UJP is APPROVED, the linked
   * shipment is not COMPLETED and nothing is pending (CR7-D1..D3). Any
   * signed-in ops user, not only the requester. Optional so a pre-CR-7 server
   * reads as "no".
   */
  canAmend?: boolean;
  /** CR-7: an approver who did not raise the pending amendment. */
  canDecideAmendment?: boolean;
  /**
   * Approver or manager on an APPROVED UJP: may send / resend the UJP's and
   * its biaya tambahan's Spend Control requests. Gates every send button.
   */
  canSendSpendRequest?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Spend Control (Basecamp payment request)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Where a transfer stands in Spend Control. The server sends the EFFECTIVE
 * state: a SENDING claim older than 10 minutes already arrives as UNKNOWN.
 *  - `SENDING` — claimed, the POST to Basecamp is in flight
 *  - `SENT`    — Basecamp created it (`id` / `requestCode` set)
 *  - `FAILED`  — Basecamp answered with an error; safe to send again
 *  - `UNKNOWN` — the connection dropped; it may exist there, resend needs `force`
 */
export type UjpSpendRequestState = 'SENDING' | 'SENT' | 'FAILED' | 'UNKNOWN';

export interface UjpSpendRequestView {
  state: UjpSpendRequestState;
  /** Basecamp document id. */
  id: string | null;
  /** e.g. `OPS-4W-PR-20261015-XYZABC`. */
  requestCode: string | null;
  /** Deep link to the request in Basecamp (null without an id). */
  url: string | null;
  /** false = Basecamp has no approval workflow for OPS/4W yet. */
  workflowConfigured: boolean | null;
  workflowMissingReason: string | null;
  claimedAt: string;
  sentAt: string | null;
  /** Basecamp's error text on FAILED. */
  error: string | null;
}

/** The UJP's own request. No money in it, so every viewer gets the block. */
export interface UjpSpendControl {
  /** false = the transfer total is 0 → nothing to send ("Tidak perlu"). */
  required: boolean;
  /** null = never sent ("Belum diajukan"). */
  request: UjpSpendRequestView | null;
  /** Basecamp list page — where to check before a forced resend. */
  listUrl: string | null;
}

/** What the list chip flags (FAILED wins over UNKNOWN server-side). */
export type UjpSpendControlIssue = 'FAILED' | 'UNKNOWN';

/** `force` is only ever sent from the UNKNOWN confirmation. */
export interface SendUjpSpendRequestPayload {
  force?: boolean;
}

/**
 * `POST /v1/ujp/:id/spend-request` and `…/amendments/:amendmentId/spend-request`.
 * The call waits for Basecamp, so `spendRequest.state` is final (SENT /
 * FAILED / UNKNOWN).
 */
export interface SendUjpSpendRequestResult {
  id: string;
  amendmentId: string | null;
  spendRequest: UjpSpendRequestView;
}

// ─────────────────────────────────────────────────────────────────────────────
// Biaya tambahan (CR-7)
// ─────────────────────────────────────────────────────────────────────────────

export type UjpAmendmentStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED';
/**
 * One post-approval extra cost (tilang, sparepart, tol tak terduga) — a CHILD
 * record of an APPROVED UJP; the UJP itself is never edited. `amount` is
 * masked (null) for a non-party viewer, the rest is visible to anyone.
 */
export interface UjpAmendment {
  id: string;
  /** 1-based, in submission order. */
  seq: number;
  amount: MoneyString | null;
  justifikasi: string;
  status: UjpAmendmentStatus;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /** Set on a REJECTED amendment (an active `UJP_REJECTION` reason). */
  reasonCode: string | null;
  /**
   * Its own Spend Control request — only ever non-null on an APPROVED
   * amendment; absent on a server without Spend Control.
   */
  spendRequest?: UjpSpendRequestView | null;
}

/** `POST /v1/ujp/:id/amendments` — amount > 0 (decimal string), justifikasi min 3. */
export interface CreateUjpAmendmentPayload {
  amount: MoneyString;
  justifikasi: string;
}

export interface CreateUjpAmendmentResult {
  id: string;
  ujpId: string;
  seq: number;
  status: UjpAmendmentStatus;
}

/** Lowercase on the wire, like the UJP decision. */
export type UjpAmendmentDecisionAction = 'approved' | 'rejected';

export interface DecideUjpAmendmentPayload {
  action: UjpAmendmentDecisionAction;
  /** Required on a reject: an ACTIVE `UJP_REJECTION` reason (LAINNYA needs a note). */
  reasonCode?: string | null;
  note?: string | null;
}

export interface DecideUjpAmendmentResult {
  id: string;
  ujpId: string;
  seq: number;
  status: UjpAmendmentStatus;
  /** Σ APPROVED amendments on the UJP after this decision. */
  amendedAmount: MoneyString;
}

/**
 * The 409 rule codes the amendment endpoints answer with (HttpExceptionFilter
 * forwards them as `code`; older gateways only carry them in `error`, which
 * `UjpApiError.message` then holds — read both via {@link ujpAmendmentErrorCode}).
 */
export type UjpAmendmentErrorCode =
  | 'UJP_NOT_APPROVED'
  | 'SHIPMENT_COMPLETED'
  | 'AMENDMENT_PENDING'
  | 'AMENDMENT_DECIDED';
export const UJP_AMENDMENT_ERROR_CODES: readonly UjpAmendmentErrorCode[] = [
  'UJP_NOT_APPROVED',
  'SHIPMENT_COMPLETED',
  'AMENDMENT_PENDING',
  'AMENDMENT_DECIDED',
];

export interface UjpClientRule {
  chargedPositioning: boolean;
  reverseCharge: MoneyString;
}

export interface UjpDetail {
  id: string;
  referenceId: string;
  status: UjpStatus;
  /** Bumps on every requester edit (CR3-D4); drives the stale-decision guard. */
  version?: number;
  client: { id: number; name: string };
  requesterEmail: string;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  header: UjpCreateHeader;
  /** `accountNumber` is `'****1234'` and `nominalTransfer` null when masked. */
  payee: UjpCreatePayee & { nominalTransfer?: MoneyString | null };
  vehicle: UjpCreateVehicle;
  costs: UjpDetailCosts;
  route: {
    routePlanId: string | null;
    routePlanName?: string | null;
    stops: UjpRouteStop[];
    legs: UjpRouteLeg[];
  };
  /** True when the plan was edited after this UJP was submitted (CR2-D17). */
  routePlanUpdatedAfterSubmit?: boolean;
  rider: UjpNamedRef | null;
  vendor: UjpNamedRef | null;
  /** Null when masked. */
  totals: UjpTotals | null;
  /** Approver-only revenue/margin (CR-4); null / absent for everyone else. */
  revenue?: UjpRevenue | null;
  /** The override flag lives on `costs` here, not on the estimate echo. */
  estimate: Omit<
    UjpEstimateResult,
    | 'totalUangJalanFlazz'
    | 'totalUangJalanQris'
    | 'totalUangJalanTransfer'
    | 'estimatedAmount'
    | 'chargedPositioningOverride'
  > | null;
  masked: boolean;
  history: UjpHistoryEntry[];
  shipment: UjpShipmentRef | null;
  viewer: UjpViewer;
  /**
   * CR-7: every biaya tambahan raised on this UJP, in submission order.
   * Optional so a pre-CR-7 server reads as "none".
   */
  amendments?: UjpAmendment[];
  /**
   * The UJP's Spend Control request (every viewer). Absent on a server
   * without Spend Control — the panel then shows no Spend Control at all.
   */
  spendControl?: UjpSpendControl | null;
  /**
   * The client rule the UJP was snapshotted under (CR-D7) — the default a
   * pool stop's `charged` falls back to (CR8-D1). `Detail UJP` enumerates the
   * envelope down to `viewer` without naming this block, so it is optional
   * rather than assumed: a UJP whose client had no config row is exactly the
   * case where the server has nothing to send, and "absent" already means
   * "pool legs not charged, reverse 0".
   */
  clientConfig?: UjpClientRule | null;
  /** Present on REJECTED rows. */
  rejection?: { reasonCode: string; reasonLabel?: string; note: string | null } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Decision / cancel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wire values are LOWERCASE (`{"action":"approved"}` in Decision - Approve /
 * Decision - Reject), not the status enum — sending `APPROVE` is a 400.
 */
export type UjpDecisionAction = 'approved' | 'rejected';

export interface UjpDecisionPayload {
  action: UjpDecisionAction;
  reasonCode?: string;
  note?: string;
  /**
   * The `version` the approver was looking at (CR3-D5). A mismatch means the
   * requester edited the UJP since — the server answers 409 `UJP_STALE`.
   */
  expectedVersion?: number;
  /** Acknowledge a known-negative margin before approving (CR4-D7). */
  confirmNegativeMargin?: boolean;
}

export interface UjpDecisionResult {
  id: string;
  status: UjpStatus;
  shipment: { id: string; waybill: string; routeCode: string } | null;
  /**
   * CR-5: plan-pinned workflows the shipment creation replaced with the slot
   * default (stale/mismatched pin). `seq` is 1-based among the SHIPPED stops.
   */
  workflowFallback?: WorkflowFallback[];
  /** Revenue outcome at approve (CR-4, approver-only). */
  revenueStatus?: UjpRevenueStatus;
  revenueAmount?: MoneyString | null;
  marginAmount?: MoneyString | null;
  /** Komisi subtracted from `marginAmount` (CR4b-D4). */
  komisiAmount?: MoneyString | null;
  /** Comma-joined {@link UjpRevenueNoteCode}s — see {@link parseRevenueNotes}. */
  revenueNote?: string | null;
  /**
   * The Spend Control claim the approve wrote (SENDING) before answering; the
   * send itself finishes in the background, so the panel reloads and polls.
   */
  spendRequest?: UjpSpendRequestView | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Masters + client config
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpClientConfig {
  clientId: number;
  chargedPositioning: boolean;
  reverseCharge: MoneyString;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface PutUjpClientConfigPayload {
  chargedPositioning: boolean;
  reverseCharge: MoneyString;
  notes?: string | null;
}

export interface UjpVehicle {
  id: string;
  plateNumber: string;
  unitType: string | null;
  energyType: UjpEnergyType;
  /** CR-D5: the per-vehicle price moved to `ujp_energy_prices` keyed by this. */
  fuelType: UjpFuelType | null;
  baseline: NumberLike | null;
  konsumsiPerKm: NumberLike | null;
  /** Default e-money on this truck (NONE/FLAZZ/QRIS/FLAZZ_QRIS); prefills the wizard. */
  defaultEMoney?: UjpEMoney | null;
  /** Physical e-money card number on this truck; prefills the wizard. The card
   *  follows the truck, so re-assigning it is a master edit. */
  eMoneyCardNo?: string | null;
  /**
   * TODO-22: the wizard list only returns active rows; the admin list passes
   * `includeInactive=true` and renders the flag.
   */
  active: boolean;
  /**
   * Pre-CR-1 field still described by the Masters - Vehicles collection doc.
   * Read as a fallback when the energy-price master has no row for the fuel
   * type / date; never required.
   */
  energyPrice?: MoneyString | null;
}

export interface UjpEnergyPrice {
  id: string;
  fuelType: UjpFuelType;
  /** Region this price applies to; null = global default. */
  region: string | null;
  price: MoneyString;
  /** `YYYY-MM-DD` — applies to deliveries on/after this day. */
  effectiveFrom: string;
}

export interface UjpSubconVendor {
  id: string;
  name: string;
  city: string | null;
  bankName: string | null;
  accountNumber: string | null;
  accountHolder: string | null;
  picName: string | null;
  picPhone: string | null;
  /** TODO-22: see {@link UjpVehicle.active}. */
  active: boolean;
}

// ── Masters admin (TODO-22, approver-gated writes) ────────────────────────────

/** `GET …/masters/vehicles` + `…/subcon-vendors`: plate/name ILIKE + the inactive rows. */
export interface UjpMasterListParams {
  search?: string;
  includeInactive?: boolean;
  /** Vehicles only: narrow to one fuel. */
  fuelType?: UjpFuelType;
  /** Vehicles only: exact unit type (case-insensitive), from `listVehicleUnitTypes`. */
  unitType?: string;
  /** 1-based page + rows per page (BE default 1 / 20, cap 200). */
  page?: number;
  limit?: number;
}

/**
 * One page of a master list, the same envelope as `/v1/addresses` and
 * `/v1/packages`: `total` is the filtered row count across every page, what
 * the `Paginator` needs.
 */
export interface UjpMasterPage<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/** `GET …/masters/energy-prices/history`: one fuel, one page. */
export interface UjpEnergyPriceHistoryParams {
  fuelType?: UjpFuelType;
  page?: number;
  limit?: number;
}

/**
 * `POST /v1/ujp/masters/vehicles` + `PATCH …/:id`. The body is a full upsert
 * (plate / energy / fuel are required on PATCH too), so a toggle sends the row
 * back with `active` flipped. 400 when an ICE row lacks `baseline > 0` or an EV
 * row lacks `konsumsiPerKm > 0`; 409 `VEHICLE_PLATE_EXISTS` on a duplicate plate.
 */
export interface UpsertUjpVehiclePayload {
  plateNumber: string;
  unitType?: string | null;
  energyType: UjpEnergyType;
  fuelType: UjpFuelType;
  /** ICE: km per litre (a JSON number — the DTO coerces with `@Type(Number)`). */
  baseline?: number | null;
  /** EV: kWh per km. */
  konsumsiPerKm?: number | null;
  /** Default e-money on this truck (NONE/FLAZZ/QRIS/FLAZZ_QRIS); prefills the wizard. */
  defaultEMoney?: UjpEMoney | null;
  /** Physical e-money card number on this truck; prefills the wizard. */
  eMoneyCardNo?: string | null;
  active?: boolean;
}

/**
 * `POST /v1/ujp/masters/energy-prices`. Prices are dated and never edited: a
 * change is a NEW row with a later `effectiveFrom`. 409 `ENERGY_PRICE_EXISTS`
 * when that fuel already has a row on that day.
 */
export interface CreateUjpEnergyPricePayload {
  fuelType: UjpFuelType;
  /** Region; omit for the global default price. */
  region?: string | null;
  price: MoneyString;
  /** `YYYY-MM-DD`. */
  effectiveFrom: string;
}

/** `POST /v1/ujp/masters/subcon-vendors` + `PATCH …/:id` (full upsert, `name` required). */
export interface UpsertUjpSubconVendorPayload {
  name: string;
  city?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  accountHolder?: string | null;
  picName?: string | null;
  picPhone?: string | null;
  active?: boolean;
}

export interface UjpReason {
  code: string;
  label: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Service error carrying the HTTP status so callers can branch on 409 ("sudah
 * diputuskan") / 404 without string-matching the message, and `aborted` so an
 * estimate the hook itself cancelled is never shown as a failure.
 */
export class UjpApiError extends Error {
  status?: number;
  aborted: boolean;
  /**
   * The machine-readable rule code the backend forwards (HttpExceptionFilter
   * TODO-24) — e.g. `UJP_STALE`, `NEGATIVE_MARGIN_CONFIRM_REQUIRED`. Lets a
   * caller tell two 409s apart without string-matching the human message.
   */
  code?: string;

  constructor(message: string, status?: number, aborted = false, code?: string) {
    super(message);
    this.name = 'UjpApiError';
    this.status = status;
    this.aborted = aborted;
    this.code = code;
  }
}

/**
 * The known amendment rule code on an error (CR-7), read from the forwarded
 * `code` first and the raw `error` message second.
 */
export const ujpAmendmentErrorCode = (
  err: unknown
): UjpAmendmentErrorCode | null => {
  if (!(err instanceof UjpApiError)) return null;
  for (const c of [err.code, err.message]) {
    if (c && (UJP_AMENDMENT_ERROR_CODES as readonly string[]).includes(c)) {
      return c as UjpAmendmentErrorCode;
    }
  }
  return null;
};

const isAbort = (error: any): boolean =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

const toError = (error: any): UjpApiError =>
  new UjpApiError(
    isAbort(error) ? 'Estimasi dibatalkan' : getBackendErrorMessage(error),
    error?.response?.status,
    isAbort(error),
    typeof error?.response?.data?.code === 'string'
      ? error.response.data.code
      : undefined
  );

// ─────────────────────────────────────────────────────────────────────────────
// Envelope
// ─────────────────────────────────────────────────────────────────────────────

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

/**
 * The list body is itself `{ data, pagination, counts }`, so a plain unwrap
 * would strip one level too many when the gateway adds the envelope and too
 * few when it doesn't. Land on whichever level carries `pagination`.
 */
const unwrapList = (payload: any): UjpListResponse => {
  const body =
    payload && typeof payload === 'object' && 'pagination' in payload
      ? payload
      : unwrap<any>(payload);
  return {
    data: body?.data ?? [],
    pagination: body?.pagination ?? {
      size: 0,
      page: 1,
      lastPage: 1,
      total: 0,
    },
    counts: body?.counts ?? {
      SUBMITTED: 0,
      APPROVED: 0,
      REJECTED: 0,
      CANCELLED: 0,
      ALL: 0,
    },
  };
};

/**
 * Masters list query: a bare string keeps the wizard's `listVehicles('B 90')`
 * call shape; the admin page passes `{ search, includeInactive }`. Empty
 * search / false flag are dropped so the URL stays as before.
 */
const toMasterListParams = (
  params?: string | UjpMasterListParams
): Record<string, string | number | boolean> | undefined => {
  const p: UjpMasterListParams =
    typeof params === 'string' ? { search: params } : (params ?? {});
  const out: Record<string, string | number | boolean> = {};
  if (p.search) out.search = p.search;
  if (p.includeInactive) out.includeInactive = true;
  if (p.fuelType) out.fuelType = p.fuelType;
  if (p.unitType) out.unitType = p.unitType;
  if (p.page) out.page = p.page;
  if (p.limit) out.limit = p.limit;
  return Object.keys(out).length > 0 ? out : undefined;
};

/**
 * The masters list envelope: the BE now returns `{ data, total, page, limit }`
 * (paged); a bare array is tolerated for an older server.
 */
const toMasterPage = <T>(body: unknown, fallbackLimit: number): UjpMasterPage<T> => {
  if (Array.isArray(body)) {
    return { data: body as T[], total: body.length, page: 1, limit: fallbackLimit };
  }
  const env = (body ?? {}) as Partial<UjpMasterPage<T>>;
  const data = env.data ?? [];
  return {
    data,
    total: typeof env.total === 'number' ? env.total : data.length,
    page: env.page ?? 1,
    limit: env.limit ?? fallbackLimit,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const ujpAPI = {
  /** Server-computed live estimate. Pass `signal` so a superseded call aborts. */
  estimate: async (
    payload: UjpEstimatePayload,
    signal?: AbortSignal
  ): Promise<UjpEstimateResult> => {
    try {
      const response = await Axios.post('/v1/ujp/estimate', payload, {
        signal,
      });
      return unwrap<UjpEstimateResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  create: async (payload: CreateUjpPayload): Promise<CreateUjpResult> => {
    try {
      const response = await Axios.post('/v1/ujp', payload);
      return unwrap<CreateUjpResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Requester edit (CR3-D2): `PUT /v1/ujp/:id` with the same body as create.
   * SUBMITTED stays SUBMITTED (`version += 1`); REJECTED resubmits to SUBMITTED.
   * 403 for anyone but the requester, 409 once the UJP is decided.
   */
  update: async (
    id: string,
    payload: CreateUjpPayload
  ): Promise<CreateUjpResult> => {
    try {
      const response = await Axios.put(`/v1/ujp/${id}`, payload);
      return unwrap<CreateUjpResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  list: async (params: UjpListParams = {}): Promise<UjpListResponse> => {
    try {
      const response = await Axios.get('/v1/ujp', { params });
      return unwrapList(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  get: async (id: string): Promise<UjpDetail> => {
    try {
      const response = await Axios.get(`/v1/ujp/${id}`);
      return unwrap<UjpDetail>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  decide: async (
    id: string,
    payload: UjpDecisionPayload
  ): Promise<UjpDecisionResult> => {
    try {
      const response = await Axios.post(`/v1/ujp/${id}/decision`, payload);
      return unwrap<UjpDecisionResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  cancel: async (id: string): Promise<{ id: string; status: UjpStatus }> => {
    try {
      const response = await Axios.post(`/v1/ujp/${id}/cancel`, {});
      return unwrap<{ id: string; status: UjpStatus }>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  // ── Biaya tambahan (CR-7) ───────────────────────────────────────────────────

  /**
   * Raise a biaya tambahan on an APPROVED UJP. Any signed-in ops user. 409
   * `UJP_NOT_APPROVED` / `SHIPMENT_COMPLETED` / `AMENDMENT_PENDING`, 400 on a
   * non-positive amount or a justifikasi under 3 characters.
   */
  createAmendment: async (
    id: string,
    payload: CreateUjpAmendmentPayload
  ): Promise<CreateUjpAmendmentResult> => {
    try {
      const response = await Axios.post(`/v1/ujp/${id}/amendments`, payload);
      return unwrap<CreateUjpAmendmentResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Finance approves / rejects one amendment — approver allowlist, never the
   * person who raised it (403); 409 `AMENDMENT_DECIDED` once it is settled.
   */
  decideAmendment: async (
    id: string,
    amendmentId: string,
    payload: DecideUjpAmendmentPayload
  ): Promise<DecideUjpAmendmentResult> => {
    try {
      const response = await Axios.post(
        `/v1/ujp/${id}/amendments/${amendmentId}/decision`,
        payload
      );
      return unwrap<DecideUjpAmendmentResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  // ── Spend Control ───────────────────────────────────────────────────────────

  /**
   * Send (or resend) the UJP's own transfer to Spend Control. Approver or
   * manager on an APPROVED UJP. Waits for Basecamp (up to ~15 s), so the
   * returned state is final. 403 / 404, 503 when Spend Control is not
   * configured, 409 `SPEND_REQUEST_SENT` / `SPEND_REQUEST_IN_PROGRESS` /
   * `SPEND_REQUEST_UNKNOWN_NEEDS_FORCE` (the message says which).
   */
  sendSpendRequest: async (
    id: string,
    payload: SendUjpSpendRequestPayload = {}
  ): Promise<SendUjpSpendRequestResult> => {
    try {
      const response = await Axios.post(`/v1/ujp/${id}/spend-request`, payload);
      return unwrap<SendUjpSpendRequestResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Same for one APPROVED biaya tambahan. */
  sendAmendmentSpendRequest: async (
    id: string,
    amendmentId: string,
    payload: SendUjpSpendRequestPayload = {}
  ): Promise<SendUjpSpendRequestResult> => {
    try {
      const response = await Axios.post(
        `/v1/ujp/${id}/amendments/${amendmentId}/spend-request`,
        payload
      );
      return unwrap<SendUjpSpendRequestResult>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  // Route plans (CR-2) moved to `services/api/routePlans.ts` — the planner is a
  // sub-domain of the route module, not the ujp module.

  // ── Client configs ──────────────────────────────────────────────────────────

  listClientConfigs: async (): Promise<UjpClientConfig[]> => {
    try {
      const response = await Axios.get('/v1/ujp/client-configs');
      const body = unwrap<{ data: UjpClientConfig[] } | UjpClientConfig[]>(
        response.data
      );
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only (403 otherwise). */
  putClientConfig: async (
    clientId: number,
    payload: PutUjpClientConfigPayload
  ): Promise<UjpClientConfig> => {
    try {
      const response = await Axios.put(
        `/v1/ujp/client-configs/${clientId}`,
        payload
      );
      return unwrap<UjpClientConfig>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  // ── Masters ─────────────────────────────────────────────────────────────────

  /**
   * Plate type-ahead (a bare `search` string, active rows only) or the admin
   * list (`{ search, includeInactive }`).
   */
  listVehicles: async (
    params?: string | UjpMasterListParams
  ): Promise<UjpVehicle[]> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/vehicles', {
        params: toMasterListParams(params),
      });
      const body = unwrap<{ data: UjpVehicle[] } | UjpVehicle[]>(response.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Distinct unit types on file (A→Z), the options of the vehicles unit filter. */
  listVehicleUnitTypes: async (): Promise<string[]> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/vehicles/unit-types');
      const body = unwrap<{ data: string[] } | string[]>(response.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** The admin list: one page of vehicles + the filtered total for the paginator. */
  listVehiclesPage: async (
    params: UjpMasterListParams
  ): Promise<UjpMasterPage<UjpVehicle>> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/vehicles', {
        params: toMasterListParams(params),
      });
      return toMasterPage<UjpVehicle>(unwrap(response.data), params.limit ?? 20);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only (403 otherwise). 201 → the created row (single envelope). */
  createVehicle: async (payload: UpsertUjpVehiclePayload): Promise<UjpVehicle> => {
    try {
      const response = await Axios.post('/v1/ujp/masters/vehicles', payload);
      return unwrap<UjpVehicle>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only. Full upsert body — see {@link UpsertUjpVehiclePayload}. */
  updateVehicle: async (
    id: string,
    payload: UpsertUjpVehiclePayload
  ): Promise<UjpVehicle> => {
    try {
      const response = await Axios.patch(`/v1/ujp/masters/vehicles/${id}`, payload);
      return unwrap<UjpVehicle>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Prices effective on `date` (`YYYY-MM-DD`), one row per fuel type. */
  listEnergyPrices: async (
    date?: string,
    region?: string | null
  ): Promise<UjpEnergyPrice[]> => {
    try {
      const params: Record<string, string> = {};
      if (date) params.date = date;
      if (region) params.region = region;
      const response = await Axios.get('/v1/ujp/masters/energy-prices', {
        params: Object.keys(params).length ? params : undefined,
      });
      const body = unwrap<{ data: UjpEnergyPrice[] } | UjpEnergyPrice[]>(response.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Every dated price row, newest `effectiveFrom` first per fuel; `fuelType`
   * narrows to one fuel. Read-only history — rows are never edited.
   */
  listEnergyPriceHistory: async (fuelType?: UjpFuelType): Promise<UjpEnergyPrice[]> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/energy-prices/history', {
        params: fuelType ? { fuelType } : undefined,
      });
      const body = unwrap<{ data: UjpEnergyPrice[] } | UjpEnergyPrice[]>(response.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** The admin history: one page of dated rows + the filtered total. */
  listEnergyPriceHistoryPage: async (
    params: UjpEnergyPriceHistoryParams
  ): Promise<UjpMasterPage<UjpEnergyPrice>> => {
    try {
      const query: Record<string, string | number> = {};
      if (params.fuelType) query.fuelType = params.fuelType;
      if (params.page) query.page = params.page;
      if (params.limit) query.limit = params.limit;
      const response = await Axios.get('/v1/ujp/masters/energy-prices/history', {
        params: Object.keys(query).length ? query : undefined,
      });
      return toMasterPage<UjpEnergyPrice>(unwrap(response.data), params.limit ?? 20);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only. 201 → the created row; 409 `ENERGY_PRICE_EXISTS`. */
  createEnergyPrice: async (
    payload: CreateUjpEnergyPricePayload
  ): Promise<UjpEnergyPrice> => {
    try {
      const response = await Axios.post('/v1/ujp/masters/energy-prices', payload);
      return unwrap<UjpEnergyPrice>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Vendor type-ahead (bare `search`, active only) or the admin list. */
  listSubconVendors: async (
    params?: string | UjpMasterListParams
  ): Promise<UjpSubconVendor[]> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/subcon-vendors', {
        params: toMasterListParams(params),
      });
      const body = unwrap<{ data: UjpSubconVendor[] } | UjpSubconVendor[]>(response.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** The admin list: one page of vendors + the filtered total (search also matches city / PIC). */
  listSubconVendorsPage: async (
    params: UjpMasterListParams
  ): Promise<UjpMasterPage<UjpSubconVendor>> => {
    try {
      const response = await Axios.get('/v1/ujp/masters/subcon-vendors', {
        params: toMasterListParams(params),
      });
      return toMasterPage<UjpSubconVendor>(unwrap(response.data), params.limit ?? 20);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only. 201 → the created row (single envelope). */
  createSubconVendor: async (
    payload: UpsertUjpSubconVendorPayload
  ): Promise<UjpSubconVendor> => {
    try {
      const response = await Axios.post('/v1/ujp/masters/subcon-vendors', payload);
      return unwrap<UjpSubconVendor>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only. Full upsert body — see {@link UpsertUjpSubconVendorPayload}. */
  updateSubconVendor: async (
    id: string,
    payload: UpsertUjpSubconVendorPayload
  ): Promise<UjpSubconVendor> => {
    try {
      const response = await Axios.patch(
        `/v1/ujp/masters/subcon-vendors/${id}`,
        payload
      );
      return unwrap<UjpSubconVendor>(response.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Rejection reasons — the shared taxonomy (`GET /v1/reasons`), which wraps
   * the list as `{ reasons: [{ code, label }] }` in BE order.
   */
  listRejectionReasons: async (): Promise<UjpReason[]> => {
    try {
      const response = await Axios.get('/v1/reasons', {
        params: { type: 'UJP_REJECTION' },
      });
      const data = unwrap<{ reasons: UjpReason[] }>(response.data);
      return data?.reasons ?? [];
    } catch (error) {
      throw toError(error);
    }
  },
};
